import React, { useState, useMemo } from 'react'
import DOMPurify from 'dompurify'
import { FileText, Code, Eye, Download, Image, Paperclip } from 'lucide-react'

interface Attachment {
  filename?: string
  content?: string
  path?: string
  contentType?: string
  cid?: string
}

interface EmailContentViewerProps {
  content?: {
    html?: string
    text?: string
    attachments?: Attachment[]
  }
  metadata?: {
    subject?: string
  }
}

const EmailContentViewer: React.FC<EmailContentViewerProps> = ({ content, metadata }) => {
  const [viewMode, setViewMode] = useState<'text' | 'html' | 'raw'>('html')
  
  // Process HTML to replace cid: references with embedded images
  const processedHtml = useMemo(() => {
    if (!content?.html) return ''
    
    let html = content.html
    
    // Replace cid: references with base64 data URLs
    if (content.attachments) {
      content.attachments.forEach(attachment => {
        if (attachment.cid && attachment.content) {
          // Convert base64 content to data URL
          const dataUrl = `data:${attachment.contentType || 'image/png'};base64,${attachment.content}`
          // Replace all occurrences of cid:xxx with the data URL
          const cidPattern = new RegExp(`cid:${attachment.cid}`, 'gi')
          html = html.replace(cidPattern, dataUrl)
        }
      })
    }
    
    // Sanitize HTML to prevent XSS
    return DOMPurify.sanitize(html, {
      ADD_TAGS: ['style'],
      ADD_ATTR: ['style', 'class', 'target', 'rel'],
      ALLOW_DATA_ATTR: true,
    })
  }, [content])
  
  // Get non-inline attachments
  const regularAttachments = useMemo(() => {
    return content?.attachments?.filter(att => !att.cid) || []
  }, [content])
  
  const inlineAttachments = useMemo(() => {
    return content?.attachments?.filter(att => att.cid) || []
  }, [content])
  
  if (!content) {
    return <div className="text-sm text-gray-500">No content available</div>
  }
  
  return (
    <div className="space-y-3">
      {/* Subject */}
      {metadata?.subject && (
        <div className="text-sm font-medium text-gray-800">
          Subject: {metadata.subject}
        </div>
      )}
      
      {/* View Mode Tabs - Responsive */}
      {content.html && (
        <div className="flex flex-wrap gap-1 sm:gap-2 border-b border-gray-200 overflow-x-auto">
          <button
            onClick={() => setViewMode('html')}
            className={`px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              viewMode === 'html' 
                ? 'border-blue-500 text-blue-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Eye className="w-3.5 h-3.5 inline mr-1" />
            <span className="hidden sm:inline">HTML View</span>
            <span className="sm:hidden">HTML</span>
          </button>
          {content.text && (
            <button
              onClick={() => setViewMode('text')}
              className={`px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                viewMode === 'text' 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="w-3.5 h-3.5 inline mr-1" />
              <span className="hidden sm:inline">Plain Text</span>
              <span className="sm:hidden">Text</span>
            </button>
          )}
          <button
            onClick={() => setViewMode('raw')}
            className={`px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              viewMode === 'raw' 
                ? 'border-blue-500 text-blue-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Code className="w-3.5 h-3.5 inline mr-1" />
            <span className="hidden sm:inline">Raw HTML</span>
            <span className="sm:hidden">Raw</span>
          </button>
        </div>
      )}
      
      {/* Content Display - Responsive Height */}
      <div className="bg-white border border-gray-200 rounded-md p-3 sm:p-4 max-h-64 sm:max-h-96 overflow-auto">
        {viewMode === 'html' && content.html ? (
          <div 
            className="prose prose-sm max-w-none text-xs sm:text-sm"
            dangerouslySetInnerHTML={{ __html: processedHtml }}
          />
        ) : viewMode === 'text' || !content.html ? (
          <pre className="text-xs sm:text-sm text-gray-600 whitespace-pre-wrap font-sans">
            {content.text || 'No text content available'}
          </pre>
        ) : viewMode === 'raw' ? (
          <pre className="text-xs text-gray-600 font-mono overflow-x-auto">
            <code>{content.html}</code>
          </pre>
        ) : null}
      </div>
      
      {/* Attachments Section */}
      {content.attachments && content.attachments.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700">
            <Paperclip className="w-4 h-4 inline mr-1" />
            Attachments ({content.attachments.length})
          </div>
          
          {/* Inline Images */}
          {inlineAttachments.length > 0 && (
            <div>
              <div className="text-xs text-gray-500 mb-1">Inline Images:</div>
              <div className="flex flex-wrap gap-1 sm:gap-2">
                {inlineAttachments.map((att, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center space-x-1 sm:space-x-2 px-1.5 sm:px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs"
                  >
                    <Image className="w-3 sm:w-3.5 h-3 sm:h-3.5 flex-shrink-0" />
                    <span className="truncate max-w-[120px] sm:max-w-none">{att.filename || `inline-${att.cid}`}</span>
                    <span className="text-blue-500 hidden sm:inline">({att.contentType?.split('/')[1] || 'image'})</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Regular Attachments */}
          {regularAttachments.length > 0 && (
            <div>
              <div className="text-xs text-gray-500 mb-1">Files:</div>
              <div className="flex flex-wrap gap-1 sm:gap-2">
                {regularAttachments.map((att, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center space-x-1 sm:space-x-2 px-1.5 sm:px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs"
                  >
                    <Paperclip className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-gray-500 flex-shrink-0" />
                    <span className="text-gray-700 truncate max-w-[120px] sm:max-w-none">{att.filename || 'Unnamed'}</span>
                    {att.contentType && (
                      <span className="text-gray-500 hidden sm:inline">({att.contentType.split('/')[1]})</span>
                    )}
                    {att.content && (
                      <button
                        onClick={() => {
                          // Create download link for base64 content
                          const blob = new Blob(
                            [Uint8Array.from(atob(att.content!), c => c.charCodeAt(0))],
                            { type: att.contentType || 'application/octet-stream' }
                          )
                          const url = URL.createObjectURL(blob)
                          const a = document.createElement('a')
                          a.href = url
                          a.download = att.filename || 'attachment'
                          a.click()
                          URL.revokeObjectURL(url)
                        }}
                        className="text-blue-600 hover:text-blue-700 p-1"
                        title="Download"
                      >
                        <Download className="w-3 sm:w-3.5 h-3 sm:h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default EmailContentViewer